import type { CSSProperties } from 'react';
import {
  Page,
  Text,
  BlockStack,
  InlineGrid,
  Button,
  InlineStack,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../providers/StoreProvider';
import { ActivateAppBanner } from '../components/ActivateAppBanner';
import { t } from '../i18n';

export type CampaignTypeKey =
  | 'spend_unlock_gift'
  | 'quantity_unlock_gift'
  | 'buy_x_get_y'
  | 'milestone_rewards';

interface CampaignTypeCard {
  key: CampaignTypeKey;
  apiType: 'FREE_GIFT' | 'BUY_X_GET_Y' | 'CART_GOAL';
  displayTitle: string;
  description: string;
  example: string;
  focus: string;
  accent: string;
}

/** Demo-store #demos card art (from shopify-demo-store demo-illustration). */
const MEDIA: CSSProperties = {
  position: 'relative',
  height: 188,
  overflow: 'hidden',
  background: 'linear-gradient(155deg, #FFF8F1 0%, #FFE8C8 45%, #FFD9A8 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // Extra bottom pad so white art card + shadow never crop
  padding: '22px 18px 26px',
  boxSizing: 'border-box',
};

const GLOW: CSSProperties = {
  position: 'absolute',
  width: '60%',
  height: '50%',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(255, 122, 0, 0.22), transparent 70%)',
  top: '20%',
  right: '10%',
  pointerEvents: 'none',
};

const GRID: CSSProperties = {
  position: 'absolute',
  inset: 0,
  opacity: 0.12,
  backgroundImage:
    'linear-gradient(rgba(255, 122, 0, 0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 122, 0, 0.5) 1px, transparent 1px)',
  backgroundSize: '22px 22px',
  maskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, #000 20%, transparent 75%)',
  WebkitMaskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, #000 20%, transparent 75%)',
  pointerEvents: 'none',
};

const ART_CARD: CSSProperties = {
  position: 'relative',
  zIndex: 1,
  width: 'min(86%, 228px)',
  maxWidth: '100%',
  background: '#fff',
  borderRadius: 16,
  border: '1px solid rgba(15, 23, 42, 0.06)',
  // Softer, shorter shadow so it stays inside the media area
  boxShadow: '0 10px 28px rgba(232, 78, 0, 0.12), 0 2px 6px rgba(15, 23, 42, 0.04)',
  padding: '14px 14px 12px',
  boxSizing: 'border-box',
  flexShrink: 0,
};

const FREE_TAG: CSSProperties = {
  display: 'inline-block',
  fontSize: 9,
  fontWeight: 800,
  letterSpacing: '0.04em',
  color: '#fff',
  background: 'linear-gradient(135deg, #FFC14A, #FF7A00)',
  padding: '2px 7px',
  borderRadius: 999,
  lineHeight: 1.2,
};

const BOX_A: CSSProperties = {
  display: 'block',
  width: 48,
  height: 48,
  borderRadius: 12,
  background: 'linear-gradient(145deg, #FFD4A0, #FF9F4A)',
  margin: '0 auto 0.25rem',
  boxShadow: '0 6px 14px rgba(255, 122, 0, 0.2)',
};

const BOX_B: CSSProperties = {
  ...BOX_A,
  background: 'linear-gradient(145deg, #FFF1D6, #FFC98A)',
};

const BOX_GIFT: CSSProperties = {
  ...BOX_A,
  width: 48,
  height: 48,
  background: 'linear-gradient(145deg, #FF7A00, #E84E00)',
  boxShadow: '0 6px 16px rgba(232, 78, 0, 0.3)',
};

const LINE: CSSProperties = {
  display: 'block',
  height: 7,
  width: '70%',
  borderRadius: 4,
  background: '#E2E8F0',
  marginBottom: 4,
};

function TypeIllustration({ typeKey }: { typeKey: CampaignTypeKey; accent?: string }) {
  // Always $ in picker art (demo copy) — not store currency
  if (typeKey === 'spend_unlock_gift') {
    // Free Gift — Auto Add (demo-store illustration: "auto-add")
    return (
      <div style={MEDIA}>
        <div style={GLOW} />
        <div style={GRID} />
        <div style={ART_CARD}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '34px 1fr auto',
              gap: 8,
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <span
              style={{
                width: 34,
                height: 34,
                borderRadius: 9,
                background: 'linear-gradient(145deg, #FFE8C8, #FFC98A)',
              }}
            />
            <div>
              <i style={LINE} />
              <i style={{ ...LINE, width: '45%' }} />
            </div>
            <b style={{ fontSize: 13, fontWeight: 700 }}>$48</b>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '34px 1fr auto',
              gap: 8,
              alignItems: 'center',
              padding: 6,
              borderRadius: 10,
              border: '1.5px solid rgba(255, 122, 0, 0.35)',
              background: '#FFFBF5',
              marginBottom: 8,
            }}
          >
            <span
              style={{
                width: 34,
                height: 34,
                borderRadius: 9,
                background: '#FFF7ED',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
                <rect x="3" y="10" width="18" height="11" rx="2" fill="#FF7A00" />
                <path d="M3 12h18" stroke="#fff" strokeWidth="1.4" />
                <path
                  d="M12 10V6M8.5 6h7"
                  stroke="#FF7A00"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <div>
              <i style={LINE} />
              <span style={FREE_TAG}>FREE</span>
            </div>
            <b style={{ fontSize: 13, fontWeight: 700, color: '#E84E00' }}>$0</b>
          </div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#E84E00',
              textAlign: 'center',
            }}
          >
            {t("⚡ Free gift auto-added")}
          </div>
        </div>
      </div>
    );
  }

  if (typeKey === 'quantity_unlock_gift') {
    const smBox = (highlight?: boolean): CSSProperties => ({
      width: highlight ? 34 : 28,
      height: highlight ? 34 : 28,
      borderRadius: 8,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 -4px',
      background: highlight
        ? 'linear-gradient(145deg, #FF7A00, #E84E00)'
        : 'linear-gradient(145deg, #FFE8C8, #FFB86B)',
      color: highlight ? '#fff' : undefined,
      fontSize: 10,
      fontWeight: 800,
      boxShadow: '0 4px 10px rgba(255, 122, 0, 0.2)',
      zIndex: highlight ? 1 : 0,
    });
    return (
      <div style={MEDIA}>
        <div style={GLOW} />
        <div style={GRID} />
        <div style={{ ...ART_CARD, textAlign: 'center', paddingTop: 16, paddingBottom: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 6,
            }}
          >
            <span style={smBox()} />
            <span style={smBox()} />
            <span style={smBox(true)}>×3</span>
          </div>
          <div style={{ fontWeight: 800, color: '#FF7A00', fontSize: 16, margin: '4px 0 8px' }}>
            +
          </div>
          <div
            style={{
              display: 'inline-block',
              position: 'relative',
              // Room for FREE badge so it is not clipped by the art card edge
              marginRight: 8,
              marginTop: 2,
            }}
          >
            <span style={BOX_GIFT} />
            <span
              style={{
                ...FREE_TAG,
                position: 'absolute',
                top: -8,
                right: -12,
              }}
            >
              FREE
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (typeKey === 'buy_x_get_y') {
    return (
      <div style={MEDIA}>
        <div style={GLOW} />
        <div style={GRID} />
        <div style={{ ...ART_CARD, paddingTop: 18, paddingBottom: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <span style={BOX_A} />
              <small style={{ fontSize: 11, fontWeight: 700, color: '#64748B' }}>{t("Buy X")}</small>
            </div>
            <span style={{ fontWeight: 800, color: '#FF7A00', fontSize: 16 }}>→</span>
            <div style={{ textAlign: 'center', position: 'relative', paddingRight: 6 }}>
              <span style={BOX_B} />
              <span
                style={{
                  ...FREE_TAG,
                  position: 'absolute',
                  top: -8,
                  right: -4,
                }}
              >
                FREE
              </span>
              <small style={{ fontSize: 11, fontWeight: 700, color: '#64748B' }}>{t("Get Y")}</small>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // milestone_rewards → progress bar (demo-store "Cart Goal Progress Bar")
  return (
    <div style={MEDIA}>
      <div style={GLOW} />
      <div style={GRID} />
      <div style={ART_CARD}>
        <p style={{ fontSize: 13, fontWeight: 700, margin: '0 0 10px' }}>
          {t("You&apos;re")} <em style={{ fontStyle: 'normal', color: '#E84E00' }}>$24</em> away
        </p>
        <div
          style={{
            position: 'relative',
            height: 5,
            background: '#F1F5F9',
            borderRadius: 999,
            margin: '0 6px 6px',
          }}
        >
          <div
            style={{
              width: '62%',
              height: '100%',
              borderRadius: 'inherit',
              background: 'linear-gradient(90deg, #FFC14A, #FF7A00)',
            }}
          />
          {[
            { left: 0, transform: 'translateX(-40%)', done: true, label: '✓' },
            { left: '62%', transform: 'translateX(-50%)', done: false, active: true, label: '🎁' },
            { right: 0, transform: 'translateX(40%)', done: false, label: '🚚' },
          ].map((n, i) => (
            <span
              key={i}
              style={{
                position: 'absolute',
                top: '50%',
                marginTop: -11,
                width: 22,
                height: 22,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                fontSize: 10,
                left: n.left as number | string | undefined,
                right: n.right as number | undefined,
                transform: n.transform,
                background: n.done ? '#FF7A00' : n.active ? '#FFF7ED' : '#fff',
                border: n.done || n.active ? '2px solid #FF7A00' : '2px solid #E2E8F0',
                color: n.done ? '#fff' : undefined,
                fontWeight: n.done ? 800 : undefined,
                boxShadow: n.active ? '0 0 0 3px rgba(255, 122, 0, 0.15)' : undefined,
              }}
            >
              {n.label}
            </span>
          ))}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 12,
            fontSize: 10,
            fontWeight: 700,
            color: '#94A3B8',
          }}
        >
          <span>10%</span>
          <span style={{ color: '#E84E00' }}>{t("Gift")}</span>
          <span>{t("Ship")}</span>
        </div>
      </div>
    </div>
  );
}

export function CampaignTypePickerPage() {
  const navigate = useNavigate();
  const { currency } = useStore();

  const cards: CampaignTypeCard[] = [
    {
      key: 'spend_unlock_gift',
      apiType: 'FREE_GIFT',
      displayTitle: t("Spend $X, Unlock a Free Gift"),
      description: t("Customers who reach a defined cart value"),
      example: t("Spend $50, get a free gift"),
      focus: t("Based on total spend"),
      accent: '#008060',
    },
    {
      key: 'quantity_unlock_gift',
      apiType: 'FREE_GIFT',
      displayTitle: t("Add X Items, Get a Free Gift"),
      description: t("Reward customers based on item count"),
      example: t("Buy 3 items, get a free gift"),
      focus: t("Item quantity focus"),
      accent: '#2c6ecb',
    },
    {
      key: 'buy_x_get_y',
      apiType: 'BUY_X_GET_Y',
      displayTitle: t("Buy X Get Y & Buy One Get One (BOGO)"),
      description: t("Purchase Product X, get Product Y free or discounted"),
      example: t("Buy a bed, get a free chair"),
      focus: t("Product-specific rewards"),
      accent: '#9c6ade',
    },
    {
      key: 'milestone_rewards',
      apiType: 'CART_GOAL',
      displayTitle: t("Milestone Rewards"),
      description: t("Spend more to unlock exciting rewards"),
      example: t("Spend $25 for 5% off, $75 for free shipping"),
      focus: t("Progress bar · multi-step"),
      accent: '#008060',
    },
  ];

  function create(card: CampaignTypeCard) {
    navigate(
      `/campaigns/new/configure?type=${card.apiType}&preset=${card.key}&currency=${currency}`,
    );
  }

  return (
    <Page
      title={t("Choose campaign type")}
      backAction={{ content: t("Campaigns"), onAction: () => navigate('/campaigns') }}
    >
      <BlockStack gap="400">
        <ActivateAppBanner />
        <Text as="p" tone="subdued">
          {t("Pick how you want to reward customers. Amounts use your store currency (")}{currency}).
        </Text>

        <InlineGrid columns={{ xs: 1, sm: 2 }} gap="400">
          {cards.map((card) => (
            <div
              key={card.key}
              style={{
                background: '#fff',
                borderRadius: 16,
                border: '1px solid #e1e3e5',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 1px 0 rgba(0,0,0,.02)',
              }}
            >
              <TypeIllustration typeKey={card.key} accent={card.accent} />
              <div
                style={{
                  padding: 20,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  flex: 1,
                }}
              >
                <Text as="h2" variant="headingMd">
                  {card.displayTitle}
                </Text>
                <Text as="p" tone="subdued">
                  {card.description}
                </Text>
                <BlockStack gap="100">
                  <InlineStack gap="100" blockAlign="center">
                    <Text as="span" variant="bodySm">
                      💡
                    </Text>
                    <Text as="span" variant="bodySm" tone="subdued">
                      {card.example}
                    </Text>
                  </InlineStack>
                  <InlineStack gap="100" blockAlign="center">
                    <Text as="span" variant="bodySm">
                      🎯
                    </Text>
                    <Text as="span" variant="bodySm" tone="subdued">
                      {card.focus}
                    </Text>
                  </InlineStack>
                </BlockStack>
                <div style={{ marginTop: 'auto', paddingTop: 12 }}>
                  <Button variant="primary" onClick={() => create(card)}>
                    {t("Create")}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </InlineGrid>
      </BlockStack>
    </Page>
  );
}
